import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import API_URL from '../config';
import { apiFetch } from '../utils/auth';
import adminStyles from './AdminPanel.module.css';
import { Input, Select, ActionsMenu, ActionsMenuDotsIcon, Modal, EmployeeTaskStatsAvatar, Avatar } from './UI';
import actionsMenuStyles from './UI/ActionsMenu.module.css';
import { useEmployeeTaskStats } from '../utils/employeeTaskStats';

// FAB «как в чатах» + стили модалки/кнопок/инпутов
import chatStyles from './Chats.module.css';
import { ReactComponent as ChatForm } from '../icons/create_chat_form.svg';
import { ReactComponent as ChatFormIcon } from '../icons/create_chat_icon.svg';

import { useRoleState, useEmployeeId } from '../context/RoleContext';

import styles from './Ratings.module.css';

// Базовый origin бэкенда: отрезаем /api/v* у API_URL
const API_ORIGIN = (() => {
  try {
    return API_URL.replace(/\/api\/v\d+\/?$/i, '');
  } catch {
    return '';
  }
})();

// Правильное построение src для аватаров
function resolveAvatar(url) {
  if (!url) return null;
  if (url.startsWith('/uploads/')) return `${API_ORIGIN}${url}`;
  return url;
}

function resolveAchievementIcon(url) {
  if (!url) return '';
  if (url.startsWith('/uploads/')) return `${API_ORIGIN}${url}`;
  return url;
}

// утилита для перестановки элементов в массиве
function moveItem(arr, from, to) {
  if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return arr;
  const copy = arr.slice();
  const [it] = copy.splice(from, 1);
  copy.splice(to, 0, it);
  return copy;
}

export default function Ratings() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Если URL-параметр series изменился (навигация назад/вперёд), синхронизируем activeId
  useEffect(() => {
    const fromUrl = Number(searchParams.get('series')) || null;
    if (fromUrl && fromUrl !== activeId) {
      setActiveId(fromUrl);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const { role } = useRoleState();
  const employeeId = useEmployeeId();
  const isAdmin = String(role).toLowerCase() === 'admin';

  const [series, setSeries] = useState([]);        // [{id, name}]
  const [activeId, setActiveId] = useState(null);  // id выбранной серии
  const [items, setItems] = useState([]);          // сотрудники текущей серии
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // --- refs для точного позиционирования индикатора ---
  const tabsWrapRef = useRef(null);
  const tabRefs = useRef([]);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  // --- состояние для DnD ---
  const [drag, setDrag] = useState({
    active: false,
    fromIndex: -1,
    overIndex: -1,
    startY: 0,
    lastY: 0,
    itemHeight: 0,
    pointerId: null,
  });
  const [menuForId, setMenuForId] = useState(null); // id сотрудника, для которого открыт «⋯»

  const employeeTaskStatsIds = useMemo(
    () => items.map((e) => e.id).filter((id) => id != null),
    [items]
  );
  const employeeTaskStatsMap = useEmployeeTaskStats(employeeTaskStatsIds);

  // ─────────────── FAB / Модалка «Новая позиция» ───────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [empQuery, setEmpQuery] = useState('');
  const [empResults, setEmpResults] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [selectedSeriesId, setSelectedSeriesId] = useState(null);
  const [position, setPosition] = useState('');

  // загрузка серий
  useEffect(() => {
    apiFetch(`${API_URL}/ratings/series`)
      .then((r) => {
        if (!r.ok) throw new Error('Не удалось получить список серий');
        return r.json();
      })
      .then((list) => {
        setSeries(list);
        const fromUrl = Number(searchParams.get('series')) || null;
        // при первом монтировании берём серию из URL, иначе — первую из списка
        setActiveId((prev) => prev ?? (fromUrl ?? (list[0]?.id ?? null)));      })
      .catch((e) => setError(e.message));
  }, []); // URL читаем однократно при монтировании

  // загрузка рейтинга выбранной серии
  useEffect(() => {
    if (activeId === null && series.length === 0) return;
    setLoading(true);
    const qs = new URLSearchParams(activeId ? { series_id: String(activeId) } : {}).toString();
    apiFetch(`${API_URL}/ratings/${qs ? `?${qs}` : ''}`)
      .then((r) => {
        if (!r.ok) throw new Error('Не удалось получить рейтинг');
        return r.json();
      })
      .then((data) => {
        setItems(data);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, [activeId, series]);

  // расчёт позиции индикатора по фактическим размерам вкладки
  useLayoutEffect(() => {
    const idx = Math.max(0, series.findIndex((x) => x.id === activeId));
    const el = tabRefs.current[idx];
    if (el) {
      setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
    }
  }, [activeId, series]);

  // пересчитать при ресайзе/повороте
  useEffect(() => {
    const onResize = () => {
      const idx = Math.max(0, series.findIndex((x) => x.id === activeId));
      const el = tabRefs.current[idx];
      if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [activeId, series]);

  // ====== DnD: обработчики для администратора ======
  // Старт перетаскивания ТОЛЬКО с «ручки» (колонка места)
  const startDragFromHandle = (e, index) => {
     if (!isAdmin) return;

    const pointerId = e.pointerId ?? 'mouse';
    // захватываем указатель на "ручке"
    e.currentTarget.setPointerCapture?.(pointerId);
    // высоту берём у всей строки (ближайший li)
    const row = e.currentTarget.closest('li');
    const h = (row && row.offsetHeight) || 60;

    setDrag({
      active: true,
      fromIndex: index,
      overIndex: index,
      startY: e.clientY,
      lastY: e.clientY,
      itemHeight: h,
      pointerId,
    });
    setMenuForId(null);
    document.body.style.touchAction = 'none';
  };

  const onDragMove = (e) => {
    if (!drag.active) return;
    const dy = e.clientY - drag.startY;
    const dir = dy > 0 ? 1 : -1;
    const threshold = Math.floor(Math.abs(dy) / (drag.itemHeight || 1));
    let target = drag.fromIndex + dir * threshold;
    target = Math.max(0, Math.min(items.length - 1, target));
    if (target !== drag.overIndex) {
      setItems((prev) => moveItem(prev, drag.overIndex, target));
      setDrag((d) => ({ ...d, overIndex: target, lastY: e.clientY }));
    }
  };

  const endDrag = async () => {
    if (!drag.active) return;
    setDrag((d) => ({ ...d, active: false }));
    document.body.style.touchAction = '';

    setSaving(true);
    try {
      // Сохраняем порядок одним вызовом /ratings/{series_id}/reorder
      await apiFetch(`${API_URL}/ratings/${activeId}/reorder`, {
        method: 'POST',
        body: JSON.stringify({ employee_ids: items.map(x => x.id) }),
      });
      // Локально обновим place
      setItems((prev) => prev.map((it, i) => ({ ...it, place: i + 1 })));
    } catch (e) {
      // в случае ошибки перечитываем список
      const qs = new URLSearchParams(activeId ? { series_id: String(activeId) } : {}).toString();
      try {
        const r = await apiFetch(`${API_URL}/ratings/${qs ? `?${qs}` : ''}`);
        const data = await r.json();
        setItems(data);
      } catch {}
    } finally {
      setSaving(false);
    }
  };

  // удаление сотрудника из серии (только для админа)
  const removeFromSeries = async (empId) => {
    if (!isAdmin) return;
    setMenuForId(null);
    setSaving(true);
    try {
      // Новый эндпоинт удаления: /ratings/{series_id}/members/{employee_id}
      await apiFetch(`${API_URL}/ratings/${activeId}/members/${empId}`, { method: 'DELETE' });
      const after = items.filter((x) => x.id !== empId);
      // Локально перенумеровываем
      setItems(after.map((x, i) => ({ ...x, place: i + 1 })));
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  // закрытие меню по клику вне
  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest?.(`.${actionsMenuStyles.wrap}`)) {
        setMenuForId(null);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  // Пока идёт DnD — слушаем pointermove/up/cancel на документе
  // Важно: зависим от drag целиком, чтобы замыкание имело актуальные значения
  useEffect(() => {
    if (!drag.active) return;
    const move = (ev) => onDragMove(ev);
    const up = () => endDrag();
    document.addEventListener('pointermove', move, { passive: false });
    document.addEventListener('pointerup', up, { once: true });
    document.addEventListener('pointercancel', up, { once: true });
    return () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', up);
    };
  }, [drag]); //  обновляем подписки на каждое изменение drag


  // ─────────────── Лайв-поиск сотрудников для модалки ───────────────
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!empQuery.trim()) {
        setEmpResults([]);
        return;
      }
      try {
        const r = await apiFetch(`${API_URL}/employees/search/?query=${encodeURIComponent(empQuery.trim())}`);
        const data = r.ok ? await r.json() : [];
        setEmpResults(Array.isArray(data) ? data : []);
      } catch {
        setEmpResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [empQuery]);

  const resetCreate = () => {
    setEmpQuery('');
    setEmpResults([]);
    setSelectedEmp(null);
    setSelectedSeriesId(activeId ?? (series[0]?.id ?? null));
    setPosition('');
  };

  const openCreate = () => {
    resetCreate();
    setCreateOpen(true);
  };

  const closeCreate = () => {
    setCreateOpen(false);
  };

  const handleCreate = async () => {
    if (!selectedEmp || !selectedSeriesId) return;
    try {
      // 1) берём текущий порядок выбранной серии
      const qs = new URLSearchParams({ series_id: String(selectedSeriesId) }).toString();
      const r = await apiFetch(`${API_URL}/ratings/${qs ? `?${qs}` : ''}`);
      const curr = r.ok ? await r.json() : [];

      // 2) если сотрудник уже есть — ничего не делаем
      if (!curr.some(x => x.id === selectedEmp.id)) {
        const placeNum = Number(position);
        const arr = curr.map(x => x.id);
        const insertAt = Number.isFinite(placeNum) && placeNum > 0 ? Math.min(placeNum - 1, arr.length) : arr.length;
        arr.splice(insertAt, 0, selectedEmp.id);

        // 3) сохраняем порядок одним запросом
        await apiFetch(`${API_URL}/ratings/${selectedSeriesId}/reorder`, {
          method: 'POST',
          body: JSON.stringify({ employee_ids: arr }),
        });
      }

      // 4) если это активная серия — перечитываем и закрываем модалку
      if (selectedSeriesId === activeId) {
        const qs2 = new URLSearchParams({ series_id: String(activeId) }).toString();
        const r2 = await apiFetch(`${API_URL}/ratings/${qs2 ? `?${qs2}` : ''}`);
        const data2 = r2.ok ? await r2.json() : [];
        setItems(data2);
      }
      setCreateOpen(false);
    } catch (e) {
      console.error(e);
      setCreateOpen(false);
    }
  };

  if (error) return <div className={styles.error}>Ошибка: {error}</div>;

  return (
    <div className={styles.page}>
      {/* Верхний таббар серий на всю ширину */}
      <div ref={tabsWrapRef} className={styles.tabsBar}>
        {series.map((s, i) => (
          <button
            key={s.id}
            ref={(el) => (tabRefs.current[i] = el)}
            className={`${styles.tabBtn} ${activeId === s.id ? styles.tabBtnActive : ''}`}
            onClick={() => {
              setActiveId(s.id);
              // обновляем query-параметр, чтобы при возврате осталась та же серия
              setSearchParams(prev => {
                const p = new URLSearchParams(prev);
                p.set('series', String(s.id));
                return p;
              });
            }}
          >
            {s.name}
          </button>
        ))}
        {series.length > 0 && (
          <div
            className={`${adminStyles.tabIndicator} ${styles.indicator}`}
            style={{
              width: indicator.width || 0,
              transform: `translateX(${indicator.left || 0}px)`,
            }}
          />
        )}
      </div>

      {/* Контент рейтинга */}
      <div className={styles.content}>
        {loading ? (
          <div className={styles.loading}>Загрузка…</div>
        ) : (
          <ul className={styles.list}>
            {items.map((e, idx) => {
              const placeShown = e.place ?? (idx + 1);
              const isLeader = placeShown === 1;
              const isDraggingThis = isAdmin && drag.active && idx === drag.overIndex;

              return (
                <li
                  key={e.id}
                  className={[
                    styles.row,
                    isDraggingThis ? styles.rowDragging : '',
                  ].join(' ')}
                >
                {/* «ручка» для перетаскивания — только по полю места */}
                  <div
                    className={`${styles.place} ${styles.placeHandle} ${isLeader ? styles.leader : ''}`}
                    onPointerDown={(ev) => isAdmin && startDragFromHandle(ev, idx)}
                  >
                    {placeShown}
                  </div>
                  <div
                    className={styles.avatarWrap}
                    onPointerDown={(ev) => ev.stopPropagation()}
                  >
                    <EmployeeTaskStatsAvatar
                      employeeId={e.id}
                      stats={employeeTaskStatsMap.get(e.id)}
                      fetchStats={false}
                      avatar={e.avatar}
                      size={44}
                      alt={e.name}
                      onClick={() => navigate(`/account/${e.id}`)}
                    />
                  </div>
                  <div className={styles.info}>
                    <div className={`${styles.name} ${isLeader ? styles.leader : ''}`}>
                      {e.name}
                    </div>

                    {!!(e.achievements && e.achievements.length) && (
                      <div className={styles.achRow}>
                        {e.achievements.map((a) => {
                          const src = resolveAchievementIcon(a.image_url);
                          if (!src) return null;
                          return (
                            <img
                              key={a.id ?? `${a.achievement_id}-${Math.random()}`}
                              src={src}
                              alt={a.title || ''}
                              title={a.title || ''}
                              className={styles.achIcon}
                              draggable={false}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {isAdmin && (
                    <ActionsMenu
                      open={menuForId === e.id}
                      onToggle={(ev) => {
                        ev.stopPropagation();
                        setMenuForId((v) => (v === e.id ? null : e.id));
                      }}
                      items={[
                        {
                          key: 'remove',
                          label: 'Удалить из серии',
                          danger: true,
                          onClick: () => removeFromSeries(e.id),
                        },
                      ]}
                      title="Действия"
                      align="left"
                      trigger={<ActionsMenuDotsIcon />}
                      wrapClassName={styles.moreWrap}
                      buttonClassName={styles.moreBtn}
                    />
                  )}
                </li>
              );
            })}
            {items.length === 0 && (
              <li className={styles.empty}>Пока нет сотрудников в этой серии</li>
            )}
          </ul>
        )}
      </div>

      {/* FAB «+» — только для админа (позиция как в чатах) */}
      {isAdmin && (
        <button
          className={chatStyles.fab}
          onClick={openCreate}
          aria-label="Добавить позицию"
          type="button"
        >
          <span className={chatStyles.fabSvgWrap} aria-hidden="true">
            <ChatForm className={chatStyles.fabBubble} />
            <ChatFormIcon className={chatStyles.fabInner} />
          </span>
        </button>
      )}

      {/* Модалка «Новая позиция» */}
      {isAdmin && createOpen && (
        <Modal
          title="Новая позиция"
          onClose={closeCreate}
          onCancel={closeCreate}
          onSubmit={handleCreate}
          submitLabel="Создать"
          submitDisabled={!selectedEmp || !selectedSeriesId}
          bodyClassName={styles.ratingsModalBody}
        >
          <div className={styles.ratingsModalForm}>
            <div>
              <label className={adminStyles.label}>
                Сотрудник <span style={{ color: '#C01048', fontWeight: "400" }}>*</span>
              </label>
              <div className={styles.relative}>
                <Input
                  className={adminStyles.fieldInput}
                  type="text"
                  placeholder="Поиск по фамилии …"
                  value={selectedEmp ? selectedEmp.name : empQuery}
                  onChange={(e) => {
                    setSelectedEmp(null);
                    setEmpQuery(e.target.value);
                  }}
                  ariaLabel="Сотрудник"
                />
                {!selectedEmp && empResults.length > 0 && (
                  <ul className={`${chatStyles.searchDropdown} ${styles.dropdownMax}`}>
                    {empResults.map((emp) => (
                      <li
                        key={emp.id}
                        className={chatStyles.searchItem}
                        onClick={() => {
                          setSelectedEmp(emp);
                          setEmpQuery('');
                          setEmpResults([]);
                        }}
                      >
                        <Avatar
                          src={resolveAvatar(emp.avatar)}
                          name={emp.name}
                          alt={emp.name}
                          size={48}
                          className={`${chatStyles.avatar} ${styles.searchAvatar}`}
                        />
                        {emp.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div>
              <label className={adminStyles.label}>
                Серия <span style={{ color: '#C01048', fontWeight: "400" }}>*</span>
              </label>
              <Select
                className={adminStyles.fieldInput}
                value={selectedSeriesId ?? (activeId ?? '')}
                onChange={(e) => setSelectedSeriesId(Number(e.target.value) || null)}
                ariaLabel="Серия"
              >
                <option value="" disabled>выберите серию</option>
                {series.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </Select>
            </div>

            <div>
              <label className={adminStyles.label}>Позиция</label>
              <Input
                className={adminStyles.fieldInput}
                type="number"
                inputMode="numeric"
                placeholder="Введи порядковый номер"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                ariaLabel="Позиция"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* индикатор сохранения порядка */}
      {saving && (
        <div className={styles.savingToast}>
          Сохраняем порядок…
        </div>
      )}
    </div>
  );
}
